/*
 * Copyright (c) 2021. Datafood srl
 * Developed by Revo Digital - revodigital.it
 * ---
 * Author: leonardoviada
 * File: selection-properties.ts
 * Project: mle-client
 * Committed last: 2021/11/12 @ 820
 * ---
 */

import {ShapeTypes} from './enums';
import {ShapeControl} from './controls/helpers/ControlGroup';

/**
 * Evita l'utilizzo di stringhe hard-coded per la gestione dei tipi di controllo forma supportati dall'work-area.
 * @enum
 */
export enum ControlTypes {
  NUMBER,
  COLOR,
  BUTTON_GROUP,
  SELECT,
  RICH_TEXT,
  TABLE_DATA,
  SIMPLE_TEXT,
  FONT_FAMILY_SELECT,
  ALIGN_SELECT,
  CHECKBOX
}

/**
 * Elenca i controlli forma supportati dall'work-area e le loro proprietò.
 * @type {{[p: string]: IShapeControl}}
 */
export const ShapeControls = {
  Y: {
    label: 'y',
    lockable: true,
    controlType: ControlTypes.NUMBER,
    displayMeasureUnit: true
  },
  X: {
    label: 'x',
    lockable: true,
    controlType: ControlTypes.NUMBER,
    displayMeasureUnit: true
  },
  WIDTH: {
    label: 'larghezza',
    icon: 'arrows-horizontal',
    lockable: true,
    controlType: ControlTypes.NUMBER,
    min: 0,
    displayMeasureUnit: true
  },
  HEIGHT: {
    label: 'altezza',
    icon: 'arrows-vertical',
    lockable: true,
    controlType: ControlTypes.NUMBER,
    min: 0,
    displayMeasureUnit: true
  },
  TABLE_WIDTH: {
    label: 'larghezza',
    icon: 'arrows-vertical',
    controlType: ControlTypes.NUMBER,
    displayMeasureUnit: true,
  },
  TABLE_HEIGHT: {
    label: 'altezza',
    icon: 'arrows-horizontal',
    controlType: ControlTypes.NUMBER,
    displayMeasureUnit: true,
  },
  ANGLE: {
    label: 'rotazione',
    icon: 'pivot',
    labelInfo: 'gradi',
    lockable: true,
    controlType: ControlTypes.NUMBER,
    min: 0,
    max: 360
  },
  BORDER_RADIUS: {
    label: 'raggio',
    labelInfo: 'pt',
    controlType: ControlTypes.NUMBER,
    min: 0,
  },
  CIRCLE_RADIUS: {
    label: 'raggio',
    controlType: ControlTypes.NUMBER,
    displayMeasureUnit: true
  },
  SKEW_X: {
    label: 'stira x',
    lockable: true,
    controlType: ControlTypes.NUMBER
  },
  SKEW_Y: {
    label: 'stira y',
    lockable: true,
    controlType: ControlTypes.NUMBER
  },
  LEVELS: {
    label: 'livelli',
    controlType: ControlTypes.BUTTON_GROUP
  },
  STROKE_WIDTH: {
    label: 'spessore',
    controlType: ControlTypes.NUMBER,
    min: 0,
    displayMeasureUnit: true
  },
  STROKE_FILL: {
    label: 'riempimento',
    icon: 'tint',
    labelInfo: 'cod. colore',
    controlType: ControlTypes.COLOR
  },
  FILL: {
    label: 'riempimento',
    icon: 'tint',
    labelInfo: 'cod. colore',
    controlType: ControlTypes.COLOR
  },
  FONT_FAMILY: {
    label: 'font',
    icon: 'font',
    controlType: ControlTypes.FONT_FAMILY_SELECT
  },
  FONT_SIZE: {
    label: 'dimensione carattere',
    labelInfo: 'pt',
    icon: 'expand-all',
    controlType: ControlTypes.NUMBER
  },
  RTF_TEXT_DATA: {
    label: 'contenuto',
    icon: 'manually-entered-data',
    controlType: ControlTypes.RICH_TEXT
  },
  SIMPLE_TEXT_DATA: {
    label: 'contenuto',
    icon: 'manually-entered-data',
    controlType: ControlTypes.SIMPLE_TEXT
  },
  COMPLEX_ROTATION: {
    label: 'rotazione',
    icon: 'manually-entered-data',
    controlType: ControlTypes.NUMBER
  },
  TABLE_DATA: {
    label: 'contenuto',
    icon: 'manually-entered-data',
    controlType: ControlTypes.TABLE_DATA
  },
  BARCODE_CONTENT: {
    label: 'contenuto',
    icon: 'manually-entered-data',
    controlType: ControlTypes.SIMPLE_TEXT
  },
  TEXT_STYLING: {
    label: 'stile testo',
    icon: 'style',
    labelInfo: 'opzioni stile',
    controlType: ControlTypes.RICH_TEXT
  }
};

/**
 * Rappresenta il tipo di associazione tra nome del controllo forma e controllo stesso.
 * Utilizzata per una tipizzazione e conseguente accesso sicuro all'associazione.
 * @interface
 */
export interface ShapeTypeControlsAssociation {
  [x: string]: ShapeControl<any>[];
}

/**
 * Raggruppa i controlli forma comuni a un dato insieme di forme.
 * @type {IShapeControl[]}
 */
export const commonShapeControls = [
  ShapeControls.LEVELS,
  ShapeControls.SKEW_X,
  ShapeControls.SKEW_Y
];

/**
 * Raggruppa i controlli forma comuni ai poligoni.
 * @type {IShapeControl[]}
 */
export const polygonControls = [
  ShapeControls.ANGLE,
  ShapeControls.WIDTH,
  ShapeControls.HEIGHT,
  ShapeControls.BORDER_RADIUS
];

export const constraintsControls = [
  ShapeControls.TABLE_WIDTH,
  ShapeControls.TABLE_HEIGHT
];

/**
 * Raggruppa i controlli forma associati ai colori di riempimento.
 * @type {IShapeControl[]}
 */
export const fillControls = [
  ShapeControls.FILL,
];

export const complexRotationControls = [
  ShapeControls.COMPLEX_ROTATION,
];

/**
 * Raggruppa i controlli forma associati al bordo e le sue principali proprietà.
 * @type {IShapeControl[]}
 */
export const strokeControls = [
  ShapeControls.STROKE_FILL,
  ShapeControls.STROKE_WIDTH,
];

/**
 * Elenca le forme per le quali è prevista un'azione su tab "Dati"
 * @type {(ShapeTypes.TABLE | ShapeTypes.BARCODE | ShapeTypes.RICH_TEXT | ShapeTypes.TEXT)[]}
 */
export const shapeTypesWithData = [ShapeTypes.TABLE, ShapeTypes.BARCODE, ShapeTypes.RICH_TEXT, ShapeTypes.TEXT, ShapeTypes.EXPORT_VARIABLE, ShapeTypes.IMAGE];

/**
 * Elenca le forme per le quali è possibile non riportare
 * le chiavi della proprietà associata nell'oggetto (fabricjs o proprietario).
 * @type {IShapeControl[]}
 */
export const shapeControlsWithoutKeySetter = [ShapeControls.TEXT_STYLING];

/**
 * Associa ciascun tipo forma ai suoi controlli forma, sfruttando anche i gruppi predefiniti
 * @type {ShapeTypeControlsAssociation}
 */
/*export const shapeTypeControlsAssociation = {
 [ShapeTypes.RECT]: [
 ...commonShapeControls,
 ...polygonControls,
 ...fillControls,
 ...strokeControls
 ],
 [ShapeTypes.TRIANGLE]: [
 ...commonShapeControls,
 ...polygonControls,
 ...fillControls,
 ...strokeControls
 ],
 [ShapeTypes.CIRCLE]: [
 ...commonShapeControls,
 ...fillControls,
 ...strokeControls,
 ShapeControls.CIRCLE_RADIUS
 ],
 [ShapeTypes.ITEXT]: [
 ...commonShapeControls,
 ...fillControls,
 ShapeControls.FONT_SIZE,
 ShapeControls.FONT_FAMILY,
 ShapeControls.RTF_TEXT_DATA,
 ShapeControls.TEXT_STYLING
 ],
 [ShapeTypes.TEXT]: [
 ...commonShapeControls,
 ...fillControls,
 ShapeControls.FONT_SIZE,
 ShapeControls.FONT_FAMILY,
 ShapeControls.SIMPLE_TEXT_DATA,
 ],
 [ShapeTypes.TABLE]: [
 ...commonShapeControls,
 ...constraintsControls,
 ...complexRotationControls,
 ShapeControls.TABLE_DATA
 ],
 [ShapeTypes.BARCODE]: [
 ...commonShapeControls,
 ...complexRotationControls,
 ShapeControls.BARCODE_CONTENT
 ]
 } as ShapeTypeControlsAssociation*/

